import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, X, Send, Bot } from "lucide-react";
import { askAssistant } from "@/lib/api";
import { T } from "@/context/LanguageContext";
import { usePlaceContext } from "@/context/PlaceContext";

const SESSION = "ask-" + Math.random().toString(36).slice(2, 10);

// minimal, safe markdown: escape HTML then apply **bold**, *italic*, links
function fmt(text) {
  const esc = String(text || "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
  return esc
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/(^|[^*])\*([^*]+)\*/g, "$1<em>$2</em>")
    .replace(/(https?:\/\/[^\s]+)/g, '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>');
}

export function AskUSAPulse() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [msgs, setMsgs] = useState([]);
  const [busy, setBusy] = useState(false);
  const place = usePlaceContext();
  const scroller = useRef(null);

  useEffect(() => {
    if (scroller.current) scroller.current.scrollTop = scroller.current.scrollHeight;
  }, [msgs, open, busy]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || busy) return;
    setMsgs((m) => [...m, { role: "user", text }]);
    setInput("");
    setBusy(true);
    try {
      const res = await askAssistant({ message: text, session_id: SESSION, geo_id: place?.current?.geo_id });
      setMsgs((m) => [...m, { role: "assistant", text: res.reply }]);
    } catch {
      setMsgs((m) => [...m, { role: "assistant", text: "Sorry, I couldn't reach the assistant just now. Please try again." }]);
    } finally {
      setBusy(false);
    }
  };

  const suggestions = place?.current
    ? [`Tell me about ${place.current.name}`, `What is ${place.current.name} known for?`, `Best things to do here`]
    : ["What can I explore on USAPulse?", "Find a state by its nickname", "Explain U.S. counties vs cities"];

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className={`fixed bottom-5 right-5 z-[70] flex items-center gap-2 rounded-full bg-oldglory-red px-5 py-3 font-semibold text-white shadow-xl transition-all hover:bg-oldglory-dark hover:shadow-2xl ${open ? "pointer-events-none opacity-0" : "opacity-100"}`}
        data-testid="ask-usapulse-btn"
      >
        <Sparkles className="h-5 w-5" /> <T>Ask USAPulse</T>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.98 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-5 right-5 z-[75] flex h-[min(600px,80vh)] w-[min(400px,calc(100vw-2.5rem))] flex-col overflow-hidden rounded-2xl border border-border bg-white shadow-2xl"
            data-testid="ask-usapulse-panel"
          >
            <div className="flex items-center justify-between bg-navy px-4 py-3 text-white">
              <span className="flex items-center gap-2 font-display font-bold">
                <Bot className="h-5 w-5 text-white" /> <T>Ask USAPulse</T>
              </span>
              <button onClick={() => setOpen(false)} className="rounded-full p-1 hover:bg-white/10" data-testid="ask-close">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div ref={scroller} className="flex-1 space-y-3 overflow-y-auto bg-parchment/40 p-4">
              {msgs.length === 0 && (
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">
                    <T>Hi! I'm your USAPulse guide. Ask me anything about U.S. places, history, government or travel.</T>
                  </p>
                  <div className="flex flex-col gap-2">
                    {suggestions.map((s) => (
                      <button key={s} onClick={() => setInput(s)} className="rounded-lg border border-border bg-white px-3 py-2 text-left text-sm text-navy transition-colors hover:border-oldglory-red" data-testid="ask-suggestion">
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {msgs.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] whitespace-pre-line rounded-2xl px-3.5 py-2 text-sm leading-relaxed ${
                    m.role === "user" ? "bg-navy text-white" : "border border-border bg-white text-ink [&_a]:text-oldglory-red [&_a]:underline"
                  }`} data-testid={`ask-msg-${m.role}`}
                    {...(m.role === "assistant" ? { dangerouslySetInnerHTML: { __html: fmt(m.text) } } : { children: m.text })}
                  />
                </div>
              ))}
              {busy && (
                <div className="flex justify-start" data-testid="ask-typing">
                  <div className="rounded-2xl border border-border bg-white px-3.5 py-2.5">
                    <span className="flex gap-1">
                      <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-oldglory-red [animation-delay:-0.2s]" />
                      <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-oldglory-red [animation-delay:-0.1s]" />
                      <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-oldglory-red" />
                    </span>
                  </div>
                </div>
              )}
            </div>

            <form onSubmit={send} className="flex items-center gap-2 border-t border-border p-3">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about any U.S. place…"
                className="flex-1 rounded-full border border-border px-4 py-2 text-sm outline-none focus:border-navy"
                data-testid="ask-input"
              />
              <button type="submit" disabled={busy || !input.trim()} className="flex h-9 w-9 items-center justify-center rounded-full bg-oldglory-red text-white transition-colors hover:bg-oldglory-dark disabled:opacity-50" data-testid="ask-send">
                <Send className="h-4 w-4" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
